import { useEffect, useRef, useCallback } from 'react';
import { driver, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '@/styles/tour.css';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { api } from '@/lib/api';

interface UseProductTourOptions {
  /** Called before the tour starts — use to expand sidebar, etc. */
  onBeforeStart?: () => void;
}

export function useProductTour(options?: UseProductTourOptions) {
  const { user, refreshUser } = useAuth();
  const isMobile = useIsMobile();
  const driverRef = useRef<Driver | null>(null);
  const hasTriggered = useRef(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const markOnboardingComplete = useCallback(async () => {
    try {
      await api.completeOnboarding();
      await refreshUser();
    } catch {
      // DB update failed — localStorage already prevents re-trigger
    }
  }, [refreshUser]);

  const startTour = useCallback(() => {
    // Mark complete immediately so the tour only ever shows once,
    // regardless of how the user interacts with it (finish, dismiss, navigate away).
    localStorage.setItem('onboarding_completed', 'true');
    markOnboardingComplete();

    optionsRef.current?.onBeforeStart?.();

    const steps: DriveStep[] = [
      {
        element: '[data-tour="sidebar-nav"]',
        popover: {
          title: 'Your research hub',
          description: 'Switch between General Research, Stock Analysis, Strategic, and Portfolio here.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="research-input"]',
        popover: {
          title: 'Ask anything',
          description: 'Type a question or topic. AI agents research it from multiple angles.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-tour="intensity-selector"]',
        popover: {
          title: 'Choose your depth',
          description: 'Quick for speed, Standard for balance, Comprehensive for deep analysis.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="session-list"]',
        popover: {
          title: 'Your research history',
          description: 'Past sessions live here. Pick up where you left off.',
          side: 'right',
          align: 'start',
        },
      },
    ];

    driverRef.current = driver({
      steps,
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.75)',
      stagePadding: 8,
      stageRadius: 12,
      popoverClass: 'vettis-tour-popover',
      onDestroyStarted: () => {
        driverRef.current?.destroy();
      },
    });

    driverRef.current.drive();
  }, [markOnboardingComplete]);

  useEffect(() => {
    if (!user) return;
    if (isMobile) return;
    if (hasTriggered.current) return;
    if (user.has_completed_onboarding) return;
    if (localStorage.getItem('onboarding_completed') === 'true') return;

    hasTriggered.current = true;
    // Delay so the dashboard DOM is fully painted
    const timer = setTimeout(() => startTour(), 800);
    return () => clearTimeout(timer);
  }, [user, isMobile, startTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
    };
  }, []);

  return { startTour };
}
